import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { finalize, from, map, switchMap } from 'rxjs';
import { BagTransportTermApiService } from '@/bag/bag-transport-term/bag-transport-term-api.service';
import { GeneratedReport, GenerateReportPayload } from '@/bag/bag-transport-term/bag-transport-term.model';
import { EMPTY_REPORT_STATUS, pollGeneratedReport } from '@/bag/bag-transport-term/_utils/poll-generated-report.util';
import { ReportPendingInfoComponent } from '@/bag/bag-transport-term/report-pending-info/report-pending-info.component';
import { TransportSegmentsApiService } from '@/bag/transport-segments/transport-segments.api.service';
import { Segment } from '@/bag/transport-segments/transport-segments.model';
import { DownloadFileService } from '@/common/download-file.service';
import { InfoboxService } from '@/common/infobox/infobox.service';

type SegmentReportKind = 'csv' | 'spreadsheet';

enum SegmentFileTypeShortcut {
	InsuranceList = 'IL',
	RoomingList = 'RL',
}

const MAX_NAME_LENGTH = 50;
const MEDICAL_INSURANCE_LIST_URL = 'medical_insurance_list';

@Component({
	selector: 'app-segment-reports-dialog',
	standalone: true,
	imports: [MatDialogModule, MatButtonModule, MatRadioModule, FormsModule, ReportPendingInfoComponent],
	templateUrl: './segment-reports-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentReportsDialogComponent implements OnInit {
	readonly segment = inject<Segment>(MAT_DIALOG_DATA);
	readonly insurers = signal<string[]>([]);
	readonly selectedInsurer = signal<string | null>(null);
	readonly insurersLoading = signal(false);
	readonly pendingReportLabel = signal<string | null>(null);

	private readonly transportSegmentsApiService = inject(TransportSegmentsApiService);
	private readonly bagTransportTermApiService = inject(BagTransportTermApiService);
	private readonly downloadFileService = inject(DownloadFileService);
	private readonly infobox = inject(InfoboxService);
	private readonly dialogRef = inject(MatDialogRef<SegmentReportsDialogComponent>);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.loadInsurers();
	}

	generateRoomingList(kind: SegmentReportKind): void {
		const payload: GenerateReportPayload = {
			name: 'RoomingListReport',
			version: 'v1',
			params: {
				segment_id: `${this.segment.id}`,
				term: {
					lower: this.segment.dateFrom,
					upper: this.segment.dateTo,
					bounds: '[)',
				},
				product: null,
			},
			output_params: this.getRoomingListOutputParams(kind),
		};

		this.startPendingReport('Trwa generowanie rooming listy');
		from(this.bagTransportTermApiService.generateReportFile(payload))
			.pipe(
				switchMap((report) =>
					pollGeneratedReport(() => this.bagTransportTermApiService.getReport(`/report/${report.id}/`)),
				),
				finalize(() => this.stopPendingReport()),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((report) => this.handleGeneratedReport(report));
	}

	downloadInsuranceList(kind: SegmentReportKind): void {
		const insurer = this.selectedInsurer();
		if (!insurer) {
			return;
		}

		const name = this.generateFileName(SegmentFileTypeShortcut.InsuranceList);
		const payload: GenerateReportPayload = {
			insurer,
			params: {
				segment_id: `${this.segment.id}`,
			},
			output_params:
				kind === 'csv'
					? { name, kind: 'CSV' }
					: {
							name,
							kind: 'SpreadSheet',
							sheetName: new Date().toLocaleString('pl-PL'),
							path: `Reports/${name}`,
						},
		};

		this.startPendingReport('Trwa generowanie listy ubezpieczeń');
		if (kind === 'csv') {
			this.bagTransportTermApiService
				.downloadReportFile(payload, MEDICAL_INSURANCE_LIST_URL)
				.pipe(
					finalize(() => this.stopPendingReport()),
					takeUntilDestroyed(this.destroyRef),
				)
				.subscribe((blob) => this.downloadFileService.downloadBlob(blob, name));
			return;
		}

		this.bagTransportTermApiService
			.downloadReportFileUrl(payload, MEDICAL_INSURANCE_LIST_URL)
			.pipe(
				finalize(() => this.stopPendingReport()),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((url) => window.open(url));
	}

	private loadInsurers(): void {
		this.insurersLoading.set(true);
		this.transportSegmentsApiService
			.getTransportTermsBySegment(this.segment.id)
			.pipe(
				map((result) => [...new Set(result.results.flatMap((term) => term.insurers ?? []))]),
				finalize(() => this.insurersLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((insurers) => {
				this.insurers.set(insurers);
				this.selectedInsurer.set(insurers[0] ?? null);
			});
	}

	private startPendingReport(label: string): void {
		this.pendingReportLabel.set(label);
		this.dialogRef.disableClose = true;
	}

	private stopPendingReport(): void {
		this.pendingReportLabel.set(null);
		this.dialogRef.disableClose = false;
	}

	private handleGeneratedReport(report: GeneratedReport | null): void {
		if (!report) {
			this.infobox.error('Nie udało się wygenerować raportu, spróbuj ponownie później.');
			return;
		}
		if (report.status === EMPTY_REPORT_STATUS) {
			this.infobox.error('Brak danych w raporcie.');
			return;
		}

		this.infobox.success('Pomyślnie wygenerowano raport.');
		window.open(report.url);
		this.dialogRef.close();
	}

	private getRoomingListOutputParams(kind: SegmentReportKind): GenerateReportPayload['output_params'] {
		const name = this.generateFileName(SegmentFileTypeShortcut.RoomingList);
		if (kind === 'csv') {
			return { name, kind: 'CSV' };
		}

		return {
			name,
			kind: 'Google Drive',
			sheetName: new Date().toLocaleString('pl-PL'),
			path: `Reports/${name}`,
		};
	}

	private generateFileName(prefix: SegmentFileTypeShortcut): string {
		return `${prefix}_${this.segment.dateFrom}_${this.segment.name}_`.slice(0, MAX_NAME_LENGTH);
	}
}
